import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHOD_STYLES,
  PAYMENT_METHOD_TYPES,
  PaymentMethodType,
} from "../../../types/impagos";
import {
  HISTORICAL_STATUSES,
  LeadStatus,
  ONBOARDING_STATUSES,
} from "../../../types/onboarding";

// Onboarding statuses first (active pipeline), then historical (complete /
// canceled / claim). Built from the canonical lists so a new lead status
// added to OnboardingCriteria + types/onboarding.ts appears here without
// touching FiltersBar.
const LEAD_STATUS_OPTIONS: LeadStatus[] = [
  ...ONBOARDING_STATUSES,
  ...HISTORICAL_STATUSES,
];

// Friendly labels; mirrors StatusChangeButton.tsx so ops sees the same
// wording across views. Unknown values fall back to the raw token.
const LEAD_STATUS_LABEL: Record<string, string> = {
  insurer_review: "Insurer review",
  docs_pending: "Docs pending",
  signature_pending: "Signature pending",
  ops_review: "Ops review",
  it_review: "IT review",
  csv_pending: "CSV pending",
  legalnote_pending: "Legal note pending",
  payment_validation_pending: "Payment validation",
  ready_to_send: "Ready to send",
  complete: "Complete",
  canceled: "Canceled",
  claim: "Claim",
};

type Props = {
  search: string;
  onSearch: (v: string) => void;
  carrier: string;
  onCarrier: (v: string) => void;
  carriers: string[];
  minAge: string;
  onMinAge: (v: string) => void;
  maxAge: string;
  onMaxAge: (v: string) => void;
  paymentMethods: PaymentMethodType[];
  onTogglePaymentMethod: (m: PaymentMethodType) => void;
  minLeadUnpaidCount: string;
  onMinLeadUnpaidCount: (v: string) => void;
  leadStatuses: LeadStatus[];
  onToggleLeadStatus: (s: LeadStatus) => void;
  onClear: () => void;
};

export default function FiltersBar({
  search,
  onSearch,
  carrier,
  onCarrier,
  carriers,
  minAge,
  onMinAge,
  maxAge,
  onMaxAge,
  paymentMethods,
  onTogglePaymentMethod,
  minLeadUnpaidCount,
  onMinLeadUnpaidCount,
  leadStatuses,
  onToggleLeadStatus,
  onClear,
}: Props) {
  const leadStatusSummary =
    leadStatuses.length === 0
      ? "Status lead (todos)"
      : `Status lead (${leadStatuses.length})`;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "0.5rem",
        background: "white",
        padding: "0.7rem 0.95rem",
        borderRadius: "0.55rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        marginBottom: "0.65rem",
      }}
    >
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Buscar por email, póliza, lead ID o invoice ID…"
        style={{ ...inputStyle, minWidth: 280 }}
      />
      <select
        value={carrier}
        onChange={(e) => onCarrier(e.target.value)}
        style={inputStyle}
      >
        <option value="">Carrier (todos)</option>
        {carriers.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <label style={labelStyle}>
        <span>Edad ≥</span>
        <input
          type="number"
          value={minAge}
          onChange={(e) => onMinAge(e.target.value)}
          placeholder="0"
          min={0}
          style={{ ...inputStyle, width: 70 }}
        />
      </label>
      <label style={labelStyle}>
        <span>Edad ≤</span>
        <input
          type="number"
          value={maxAge}
          onChange={(e) => onMaxAge(e.target.value)}
          placeholder="∞"
          min={0}
          style={{ ...inputStyle, width: 70 }}
        />
      </label>
      <div style={{ display: "inline-flex", gap: "0.3rem", alignItems: "center" }}>
        <span style={{ fontSize: ".72rem", color: "#73879C", fontWeight: 600 }}>
          Pago
        </span>
        {PAYMENT_METHOD_TYPES.map((m) => {
          const active = paymentMethods.includes(m);
          const palette = PAYMENT_METHOD_STYLES[m];
          return (
            <button
              key={m}
              type="button"
              onClick={() => onTogglePaymentMethod(m)}
              style={{
                padding: "0.3rem 0.65rem",
                borderRadius: "0.35rem",
                fontSize: ".75rem",
                fontWeight: 600,
                cursor: "pointer",
                border: `1px solid ${active ? palette.fg : "#dee2e6"}`,
                background: active ? palette.bg : "white",
                color: active ? palette.fg : "#73879C",
              }}
            >
              {PAYMENT_METHOD_LABEL[m]}
            </button>
          );
        })}
      </div>
      <label style={labelStyle}>
        <span>Impagos lead ≥</span>
        <input
          type="number"
          value={minLeadUnpaidCount}
          onChange={(e) => onMinLeadUnpaidCount(e.target.value)}
          placeholder="2"
          min={2}
          style={{ ...inputStyle, width: 70 }}
        />
      </label>
      {/*
        Native <details> popover instead of a custom dropdown so it stays
        accessible (keyboard + screen reader) and zero-dep. Clicking outside
        closes via the browser's default behaviour; checkbox toggles fire
        onToggleLeadStatus immediately so the table refetches on each pick
        — same UX as the payment chips above.
      */}
      <details style={detailsStyle}>
        <summary style={summaryStyle}>{leadStatusSummary}</summary>
        <div style={leadStatusPopoverStyle}>
          {LEAD_STATUS_OPTIONS.map((s) => {
            const checked = leadStatuses.includes(s);
            return (
              <label key={s} style={leadStatusOptionStyle}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleLeadStatus(s)}
                />
                <span>{LEAD_STATUS_LABEL[s] ?? s}</span>
              </label>
            );
          })}
        </div>
      </details>
      <button type="button" onClick={onClear} style={ghostBtn}>
        Limpiar filtros
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
};

const labelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.3rem",
  fontSize: ".78rem",
  color: "#73879C",
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};

const detailsStyle: React.CSSProperties = {
  position: "relative",
};

const summaryStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
  color: "#1f2933",
  cursor: "pointer",
  listStyle: "none",
};

const leadStatusPopoverStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 20,
  background: "white",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  padding: "0.5rem 0.65rem",
  minWidth: 200,
  display: "flex",
  flexDirection: "column",
  gap: "0.3rem",
  maxHeight: 320,
  overflowY: "auto",
};

const leadStatusOptionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  fontSize: ".82rem",
  color: "#1f2933",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
